import { FC, useEffect, useState, useCallback } from 'react';
import { Card, Row, Col, Spin, Alert, Switch, Space } from 'antd';
import SimulationForm from '../../components/SimulationForm';
import SimulationHistoryList from '../../components/SimulationHistoryList';
import PriceChart from '../../components/PriceChart';
import { fetchActiveSimulations, fetchAllSimulations, SimulationResult, PricePoint, PriceSimulationWithPointCount } from '../../api/priceSimulation';

const PriceSimulationPage: FC = () => {
  const [loading, setLoading] = useState(true);
  const [simulations, setSimulations] = useState<SimulationResult[]>([]);
  const [simulationsWithCount, setSimulationsWithCount] = useState<PriceSimulationWithPointCount[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<PricePoint[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [selectedSimulation, setSelectedSimulation] = useState<SimulationResult | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (showHistory) {
        const data = await fetchAllSimulations();
        setSimulationsWithCount(data);
      } else {
        const data = await fetchActiveSimulations();
        setSimulations(data);
        
        // 如果有模拟数据且没有选中的模拟，自动选择第一个
        if (data.length > 0 && !selectedSimulation) {
          setSelectedSimulation(data[0]);
        }
      }
    } catch (e) {
      console.error('加载价格模拟失败', e);
      setError('加载价格模拟数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [selectedSimulation, showHistory]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePreview = (data: PricePoint[]) => {
    setPreviewData(data);
    setPreviewLoading(false);
  };


  const handleSelect = (record: SimulationResult) => {
    setSelectedSimulation(record);
    setShowHistory(false); // 切换到编辑模式
    // 滚动到页面顶部的设置表单
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSuccessOrReset = () => {
    setSelectedSimulation(null);
    loadData();
  };

  const handleHistoryToggle = (checked: boolean) => {
    setShowHistory(checked);
    if (checked) {
      setSelectedSimulation(null); // 清除选中的模拟
    }
  };


  return (
    <div className="price-simulation-page">
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card 
            title={selectedSimulation ? `编辑模拟: ${selectedSimulation.productCode}` : "价格模拟设置"}
            extra={
              <Space>
                <span>查看历史记录</span>
                <Switch 
                  checked={showHistory}
                  onChange={handleHistoryToggle}
                  disabled={!!selectedSimulation}
                />
              </Space>
            }
          >
            {!showHistory && (
              <SimulationForm 
                onPreview={handlePreview}
                onSuccess={handleSuccessOrReset}
                initialValues={selectedSimulation}
                onReset={() => setSelectedSimulation(null)}
              />
            )}
            {showHistory && (
              <Alert 
                type="info" 
                message="历史记录模式" 
                description="在此模式下，您可以查看所有历史记录，管理记录状态和数据。" 
                showIcon
              />
            )}
          </Card>
        </Col>
        
        {previewData.length > 0 && !showHistory && (
          <Col span={24}>
            <PriceChart data={previewData} loading={previewLoading} />
          </Col>
        )}
        
        <Col span={24}>
          {loading ? (
            <Card>
              <Spin tip="加载价格模拟数据..." />
            </Card>
          ) : error ? (
            <Alert type="error" message={error} />
          ) : (
            <SimulationHistoryList
              simulations={showHistory ? undefined : simulations}
              simulationsWithCount={showHistory ? simulationsWithCount : undefined}
              onRefreshNeeded={loadData}
              onView={handleSelect}
              showHistoryMode={showHistory}
            />
          )}
        </Col>
      </Row>
    </div>
  );
};

export default PriceSimulationPage; 